// Moeda real de cada item no Solicitar Adiantamento (D-0117, Rafael 27/10): "na tela do adiantamento preciso que
// mostre a moeda real no orçamento e o valor convertido no câmbio da etapa do orçamento: pré-evento, negociado ou
// fechamento. E para mandar ao financeiro no valor das somas fica o real com base no câmbio."
//
// Etapa = a vigente do FORNECEDOR (última coluna preenchida no agregado dele — a mesma régua do saldo da Previsão
// e do Efetivo; colunaVigentePolicy espelha OrcamentoColunaVigente da API). Moeda e câmbio = os da linha nessa etapa
// (moedaFornecedorPolicy: herança Pré → Negociado → Fechamento; legado = moeda e câmbio do orçamento).
// Antes: sempre o Pré-evento × câmbio do topo — item em € num orçamento em US$ saía convertido pelo dólar e item em
// US$ num orçamento em R$ saía como se fosse real.
import { estadoLinhaColunaVigente, etapaEstimativaLinha, resolverColunaVigentePorFornecedor, ROTULO_ETAPA_VIGENTE } from './colunaVigentePolicy';
import { cambioBrlLinha, cambioGlobalOrcamento, moedaDaLinhaFornecedor, simboloMoeda } from './moedaFornecedorPolicy';
import { moeda } from '../../util/util';

// D-0126 (Rafael 28/10): o valor de cada item é o CHEIO — total da linha (já com % Impostos e NF) × câmbio + encargos
// do pagamento no exterior (IOF/IR, envio e NF Access), nunca o FEE. A API calcula (AdiantamentoValorCheio, show →
// `adiantamentoValores`) e é ela que grava; a tela só lê. Etapa: a vigente do fornecedor ou uma escolhida pelo operador
// ("está no Negociado, mas quer pedir com base no Pré-evento").
export const ETAPA_VIGENTE = 'vigente';
export const ESCOLHAS_ETAPA = [
    { valor: ETAPA_VIGENTE, rotulo: 'Etapa do fornecedor' },
    { valor: 'PreEvento', rotulo: 'Pré-evento' },
    { valor: 'Negociado', rotulo: 'Negociado' },
    { valor: 'Fechamento', rotulo: 'Fechamento' },
];

const numero = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const arred = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;

// Momento cujo valor compõe a linha na etapa (Fechamento de exterior aberto = estimativa Negociado/Pré).
function momentoDoValor(linha, etapa, estado) {
    if (etapa === 'Fechamento' && estado.estado === 'aberta') {
        return etapaEstimativaLinha(linha, 'Fechamento') === 'neg' ? 'Negociado' : 'PreEvento';
    }
    return etapa;
}

/**
 * Valor cheio de cada subitem na etapa escolhida (padrão: a vigente do fornecedor), como a API calculou.
 * Sem `adiantamentoValores` (API anterior à D-0126): a conta antiga da D-0117, sem encargos, só na etapa vigente.
 * @return {Object<string, {etapa, etapaRotulo, moeda, simbolo, valorOrigem, cambio, encargosBrl, valorBrl}>} por id do subitem
 */
export function valoresAdiantamentoPorSubitem(orcamento = {}, escolha = ETAPA_VIGENTE) {
    const daApi = orcamento.adiantamentoValores?.[escolha];
    if (daApi && typeof daApi === 'object') {
        const resultado = {};
        Object.entries(daApi).forEach(([id, v]) => {
            const moedaLinha = v.moeda || 'real';
            resultado[String(id)] = {
                etapa: v.etapa, etapaRotulo: ROTULO_ETAPA_VIGENTE[v.etapa] || v.etapa,
                moeda: moedaLinha, simbolo: simboloMoeda(moedaLinha), valorOrigem: arred(numero(v.valor_origem)),
                cambio: numero(v.cambio), encargosBrl: arred(numero(v.encargos_brl)), valorBrl: arred(numero(v.valor_brl)),
            };
        });
        return resultado;
    }
    return escolha === ETAPA_VIGENTE ? valoresSemEncargos(orcamento) : {};
}

// Conta da D-0117 (sem encargos): fallback enquanto a API não manda `adiantamentoValores`.
function valoresSemEncargos(orcamento = {}) {
    const linhas = [];
    (orcamento.orcamentoitem || []).forEach((grupo) => (grupo.subitem || []).forEach((sub) => linhas.push(sub)));
    if (linhas.length === 0) return {};
    const cambioGlobal = cambioGlobalOrcamento(orcamento);
    const moedaOrc = orcamento.moeda || 'real';
    const r = resolverColunaVigentePorFornecedor(linhas, { cambio: cambioGlobal, emBrl: true });
    const resultado = {};
    linhas.forEach((linha, i) => {
        const etapa = r.etapas[i];
        const estado = estadoLinhaColunaVigente(linha);
        const momento = momentoDoValor(linha, etapa, estado);
        const moedaLinha = moedaDaLinhaFornecedor(linha, moedaOrc, momento);
        const valorBrl = arred(r.valores[i]);
        const valorOrigem = estado.estado === 'ledger' && etapa === 'Fechamento'
            ? arred(numero(linha.totalFechamento))
            : arred(numero(linha[`total${momento}`]));
        // Câmbio da linha na etapa (o R$ arredondado ÷ valor dava "6,820009"); só o ledger usa o câmbio real pago.
        const ledger = estado.estado === 'ledger' && etapa === 'Fechamento';
        const cambio = moedaLinha === 'real' ? 1
            : (ledger && valorOrigem > 0 ? valorBrl / valorOrigem : cambioBrlLinha(linha, momento, cambioGlobal));
        resultado[String(linha.id)] = {
            etapa, etapaRotulo: ROTULO_ETAPA_VIGENTE[etapa] || etapa,
            moeda: moedaLinha, simbolo: simboloMoeda(moedaLinha), valorOrigem, cambio, encargosBrl: 0, valorBrl,
        };
    });
    return resultado;
}

// "US$ 1.000,00 × 5,2000" (null para item em R$ sem taxas; com taxas, "R$ 800,00" — a etiqueta das taxas explica o resto).
export function textoValorNaMoeda(info) {
    if (!info) return null;
    if (info.moeda === 'real') return numero(info.encargosBrl) > 0 ? 'R$ ' + moeda(info.valorOrigem) : null;
    return info.simbolo + ' ' + moeda(info.valorOrigem) + ' × '
        + Number(info.cambio || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 6 });
}

// Etiqueta das taxas do item para o operador (Rafael 28/10: "precisa de alguma forma aparecer as taxas em formato de
// label para o operador"): "+ R$ 2.243,36 de taxas". null sem taxas. O e-mail e o Detalhe do Financeiro não quebram.
export function textoTaxasItem(encargosBrl) {
    return numero(encargosBrl) > 0 ? '+ R$ ' + moeda(encargosBrl) + ' de taxas' : null;
}

// Soma das taxas dos itens marcados (linha "Taxas incluídas" do resumo).
export function totalTaxas(itens = []) {
    return arred(itens.reduce((s, item) => s + numero(item.encargosItem), 0));
}

// "% do valor do orçamento" (D-0146, Rafael 02/11): 100% = soma do valor cheio de TODOS os itens que podem ir para o
// adiantamento, na etapa escolhida, sem FEE — marcados ou não, já pedidos ou não. Antes dividia pelo valorTotal do
// orçamento × câmbio, sem as taxas do exterior e na etapa do topo: US$ 3.200 pedidos por inteiro (R$ 17.347,84 +
// R$ 2.678,79 de taxas = R$ 20.026,63) saíam como 115,4%. Recebe a lista já na etapa (itensNaEtapa). null sem base.
export function percentualDoOrcamento(valorSolicitado, itens = []) {
    const base = arred(itens.reduce((s, item) => s + numero(item.valorPreEvento), 0));
    const valor = numero(valorSolicitado);
    if (base <= 0 || valor <= 0) return null;
    return Math.round((valor / base) * 1000) / 10;
}

// Quebra por moeda dos itens (nunca soma moedas diferentes): [{moeda, simbolo, valorOrigem, valorBrl}],
// estrangeiras primeiro, R$ por último.
export function quebraPorMoeda(itens = []) {
    const acc = new Map();
    itens.forEach((item) => {
        const m = item.moedaItem || 'real';
        if (!acc.has(m)) acc.set(m, { moeda: m, simbolo: simboloMoeda(m), valorOrigem: 0, valorBrl: 0 });
        const q = acc.get(m);
        q.valorOrigem += numero(item.valorOriginal);
        q.valorBrl += numero(item.valorPreEvento);
    });
    return [...acc.values()]
        .map((q) => ({ ...q, valorOrigem: arred(q.valorOrigem), valorBrl: arred(q.valorBrl) }))
        .sort((a, b) => (a.moeda === 'real') - (b.moeda === 'real'));
}

// "US$ 1.000,00 + € 200,00 = R$ 6.400,00" (null quando tudo está em R$).
export function textoQuebraPorMoeda(itens = []) {
    const quebra = quebraPorMoeda(itens);
    if (!quebra.some((q) => q.moeda !== 'real')) return null;
    const total = quebra.reduce((s, q) => s + q.valorBrl, 0);
    return quebra.map((q) => q.simbolo + ' ' + moeda(q.valorOrigem)).join(' + ') + ' = R$ ' + moeda(total);
}

// Algum item fora do real (banner de câmbio da tela).
export function temItemEmOutraMoeda(itens = []) {
    return itens.some((item) => (item.moedaItem || 'real') !== 'real');
}

// Itens da lista na etapa escolhida (D-0126): cada item carrega `valoresPorEscolha` (as 4 escolhas, da API) e recebe os
// campos da etapa; some da lista o que não tem valor nela. Seleção e selos vêm do próprio item (a troca de etapa preserva).
export function itensNaEtapa(lista = [], escolha = ETAPA_VIGENTE) {
    return lista.map((item) => {
        const info = item.valoresPorEscolha?.[escolha];
        if (!info) return null;
        return {
            ...item,
            valorOriginal: info.valorOrigem, // na moeda real do item (USD, EUR…), na etapa
            valorPreEvento: info.valorBrl,   // nome legado (somas e complemento leem): R$ cheio na etapa
            moedaItem: info.moeda, simboloItem: info.simbolo, cambioItem: info.cambio,
            encargosItem: info.encargosBrl, etapaItem: info.etapaRotulo, etapaValor: info.etapa,
        };
    }).filter((item) => item && item.valorPreEvento > 0);
}
